import { Page } from '@components/Page'
import { Breadcrumbs } from '@components/_rcpo/Breadcrumbs'
import { RCPOPage } from '@components/_rcpo/Page'
import ProductInfo from '@components/_rcpo/ProductInfo'
import { hasSufficientProductData } from '@components/_rcpo/ProductOverview/helpers/has-sufficient-product-data'
import MayAlsoLike from '@components/_rcpo/Sliders/MayAlsoLike'
import ProductProperties from '@components/_rcpo/ProductProperties'
import { SchemaOrg } from '@components/schema-org'
import { Article, ArticleDocument } from '@generated'
import { buildLanguageAlternateNavigation } from '@lib/build-language-alternates'
import { getRcpoModelSeoAttributes } from '@lib/get-rcpo-model-seo-attributes'
import { getServerSideTranslations } from '@lib/get-server-side-translations'
import { Item, SingleItemResponse, getPageByCodename } from '@lib/kontent'
import { RolexRcpoModelPageV7 } from '@lib/kontent/models/RolexRcpoModelPageV7'
import { createUrqlClient } from '@lib/urqlClient'
import { GetStaticProps, NextPage } from 'next'
import { useRouter } from 'next/router'
import slugify from 'slugify'
import { cn } from '@lib/cn'
import { RolexKeepExploringSliderItem } from '@lib/kontent/models/RolexKeepExploringSliderItem'
import { RCPO_ATTRIBUTE_ID_COLLECTION } from '@components/_rcpo/ProductOverview/hooks/use-rcpo-data'
import { ProductCertification } from '@components/_rcpo/ProductCollection/ProductCertification'
import ProductCollectionInfo from '@components/_rcpo/ProductCollection/ProductCollectionInfo'
import { RolexRcpoCollectionBannerItemV7 } from '@lib/kontent/models/RolexRcpoCollectionBannerItemV7'
import { RolexRcpoModelPage } from '@lib/kontent/models/RolexRcpoModelPage'
import KeepExploring from '@components/_rolex/sliders/KeepExploring'
import { RolexContactFormModule } from '@components/_rolex/_contact-form/Module'
import { RolexRcpoContactPageV7 } from '@lib/kontent/models/RolexRcpoContactPageV7'
import { RolexContainer } from '@components/_rolex/Container'
import { Heading } from '@components/rolex-ui/Heading'
import { useTranslation } from 'next-i18next'
import { RolexSendUsAMessage } from '@lib/kontent/models/RolexSendUsAMessage'
import { IS_ROLEX_V7_ENABLED } from '@lib/features'
import RCPOWatchV6 from '@pages/rolex-certified-pre-owned-v6/watches/[pid]'

type RCPOWatchProps = SingleItemResponse<any> & {
  data: Article
  contactPageContent?: SingleItemResponse<RolexSendUsAMessage> // Mandatory for V7, inexistent for V6
}
// RolexRcpoModelPageV7 | RolexRcpoModelPage

const collectionNameToKey: Record<string, keyof RolexRcpoModelPage> = {
  'Day-Date': 'day_date_item',
  'Sky-Dweller': 'sky_dweller_item',
  'Lady-Datejust': 'lady_datejust_item',
  Datejust: 'datejust_item',
  'Oyster Perpetual': 'oyster_perpetual_item',
  'Cosmograph Daytona': 'cosmograph_daytona_item',
  Deepsea: 'deepsea_item',
  'Sea-Dweller': 'sea_dweller_item',
  Submariner: 'submariner_item',
  'GMT-Master': 'gmt_master_item',
  'GMT-Master II': 'gmt_master_ii_item',
  'Yacht-Master': 'yacht_master_item',
  'Yacht-Master II': 'yacht_master_ii_item',
  Explorer: 'explorer_item',
  'Explorer II': 'explorer_ii_item',
  Milgauss: 'milgauss_item',
  'Air-King': 'air_king_item',
  Cellini: 'cellini_item',
}

const RCPOWatch: NextPage<RCPOWatchProps> = ({
  data,
  item,
  contactPageContent,
  modular_content,
}) => {
  const { title, description } = getRcpoModelSeoAttributes(data)
  const router = useRouter()
  const slug = `${slugify(data.modelName as string, { lower: true })}-${data.id as string}`
  const { t } = useTranslation('rolex')

  if (!IS_ROLEX_V7_ENABLED) {
    return <RCPOWatchV6 {...{ data, item, contactPageContent, modular_content }} />
  }

  const keepExploringItems = item.elements.keep_exploring_items.value.map(
    (key) => modular_content[key].elements,
  ) as RolexKeepExploringSliderItem[]
  const collectionName = data.articleAttributes.find(
    (attr) => attr.attributeID === RCPO_ATTRIBUTE_ID_COLLECTION,
  )?.attributeValue

  const key = collectionNameToKey[collectionName]
  const modularContentKey = item.elements[key]?.value[0] as string | undefined
  const modularContentItem = modularContentKey
    ? (modular_content[modularContentKey] as Item<RolexRcpoCollectionBannerItemV7>)
    : null

  return (
    <Page
      title={title}
      description={description as string}
      languageAlternates={buildLanguageAlternateNavigation(
        router.locales,
        'rcpo.selection.url',
        null,
        `/${slug}`,
      )}
    >
      <SchemaOrg variant="rolex-rcpo-product" data={data} />
      <RCPOPage
        pageType="model page"
        productFamily={collectionName.toLowerCase().replace(' ', '-')}
        productRMC={data.articleAttributes.find((attr) => attr.attributeID === 59)?.attributeValue}
      >
        <Breadcrumbs
          items={['index', 'selection']}
          currentPageTitle={title.replace('Rolex Certified Pre-Owned', '').replace(' | GASSAN', '')}
        />
        <div
          className={cn(
            'mb-[60px] flex flex-col gap-y-[60px] bg-rolexLightBeige py-[60px]',
            'lg:mb-[90px] lg:gap-y-[90px] lg:py-[90px]',
          )}
        >
          <ProductInfo data={data} />
          <ProductCertification item={item} />
          <ProductProperties data={data} />
          {modularContentItem && <ProductCollectionInfo modularContentItem={modularContentItem} />}
          <RolexContainer>
            <Heading variant="headline36" className="mb-5 text-rolexBrown">
              {t('contactUs')}
            </Heading>
            <RolexContactFormModule item={contactPageContent.item} backgroundColor="white" />
          </RolexContainer>
          <MayAlsoLike currentProductId={data.id} />
        </div>
        <KeepExploring items={keepExploringItems} />
      </RCPOPage>
    </Page>
  )
}

export const getStaticProps: GetStaticProps = async ({ locale, params, preview }) => {
  const id = (params?.pid as string).split('-').pop()
  const lang = locale === 'de' ? 'en' : locale
  const client = createUrqlClient(lang, true)
  try {
    const { data } = await client.query(ArticleDocument, { id }).toPromise()

    let content: SingleItemResponse<RolexRcpoModelPageV7 | RolexRcpoModelPage>
    let contactPageContent: SingleItemResponse<RolexRcpoContactPageV7> | null = null

    if (!IS_ROLEX_V7_ENABLED) {
      content = await getPageByCodename<RolexRcpoModelPage>({
        codename: 'rolex_rcpo___model_page',
        lang: lang,
        preview,
      })
    } else {
      content = await getPageByCodename<RolexRcpoModelPageV7>({
        codename: 'rolex_rcpo_model_page_v7',
        lang: lang,
        preview,
      })
      contactPageContent = await getPageByCodename<RolexRcpoContactPageV7>({
        codename: 'rolex___send_us_a_message',
        lang: lang,
        preview,
      })
    }

    const hasNeededData = hasSufficientProductData(data.article)

    if (!data.article || !data.article.id || !hasNeededData) {
      return { notFound: true }
    }
    return {
      props: {
        ...(await getServerSideTranslations(lang)),
        ...content,
        contactPageContent,
        data: data.article,
      },
      revalidate: 10 * 60, // Revalidate every 10 minutes
    }
  } catch (error) {
    throw error
  }
}

export const getStaticPaths = async () => {
  return {
    fallback: 'blocking',
    paths: [],
  }
}

export default RCPOWatch
